import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  AddBusinessOutlined,
  AddShoppingCartOutlined,
  AccountBalanceWalletOutlined,
  CalculateOutlined,
  CompareArrowsOutlined,
  ErrorOutlineOutlined,
  GroupAddOutlined,
  Inventory2Outlined,
  LocalGasStationOutlined,
  PeopleOutlineOutlined,
  PropaneTankOutlined,
  StraightenOutlined,
  TrendingDownOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';

import API from '../data/api';
import AllOrders from '../components/logistics/allOrders';
import BuyModal from '../components/logistics/modals/buyModal';
import TransportCompany from '../components/logistics/modals/TransportCompany';
import InterDepotTankTransfer from '../components/logistics/modals/InterDepotTankTransfer';
import { NewProductLine } from '../components/logistics/modals/NewProductLine';
import { useSnackbar } from '../contexts/SnackbarContext';
import { getUserInfo, isAuthorizedRole } from '../utils/authUtil';

const ALL_DEPOTS_VALUE = 'ALL';

const CARD_COLOR_TOKENS = [
  { accent: '#2563eb', surface: '#eff6ff' },
  { accent: '#0284c7', surface: '#f0f9ff' },
  { accent: '#1d4ed8', surface: '#eef2ff' },
  { accent: '#334155', surface: '#f8fafc' },
];

const getCardTone = (index) => CARD_COLOR_TOKENS[index % CARD_COLOR_TOKENS.length];

const TABLE_PANEL_SX = {
  border: '1px solid',
  borderColor: '#e2e8f0',
  borderRadius: 0,
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  backgroundColor: '#ffffff',
  '&::-webkit-scrollbar': {
    height: 10,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#cbd5e1',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f5f9',
  },
};

const TABLE_SECTION_HEADER_SX = {
  px: 2,
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
  background: 'linear-gradient(90deg, #f8fafc 0%, #eef2ff 100%)',
};

const DATA_TABLE_SX = {
  width: 'max-content',
  minWidth: '100%',
  tableLayout: 'auto',
  '& .MuiTableCell-root': {
    whiteSpace: 'nowrap',
    overflow: 'visible',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: '#f8fafc',
    color: '#334155',
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.65,
    borderBottom: '1px solid #dbe4ef',
    py: 1.15,
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottomColor: '#e2e8f0',
    py: 1.1,
  },
};

const TABLE_PAGINATION_SX = {
  borderTop: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#f8fafc',
};

const getStripedRowSx = (rowIndex, options = {}) => ({
  backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8fbff',
  transition: 'background-color 180ms ease',
  ...(options.clickable ? { cursor: 'pointer' } : {}),
  '&:hover': {
    backgroundColor: options.disableHover ? undefined : '#eaf2ff',
  },
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) => `BWP ${currencyFormatter.format(toNumber(value))}`;
const formatAmount = (value) => currencyFormatter.format(toNumber(value));
const formatLabel = (value) =>
  String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const GRADE_DISPLAY_ORDER = ['unleaded', 'leaded', 'diesel'];

const formatGradeLabel = (value) => formatLabel(String(value || '').toLowerCase());

const getOrderedGradeKeys = (valueMap = {}) =>
  Object.keys(valueMap || {}).sort((left, right) => {
    const leftRank = GRADE_DISPLAY_ORDER.indexOf(String(left).toLowerCase());
    const rightRank = GRADE_DISPLAY_ORDER.indexOf(String(right).toLowerCase());
    const normalizedLeftRank = leftRank === -1 ? Number.MAX_SAFE_INTEGER : leftRank;
    const normalizedRightRank = rightRank === -1 ? Number.MAX_SAFE_INTEGER : rightRank;

    if (normalizedLeftRank !== normalizedRightRank) {
      return normalizedLeftRank - normalizedRightRank;
    }
    return String(left).localeCompare(String(right));
  });

const formatFuelMetricEntries = (valueMap, options = {}) => {
  if (!valueMap || typeof valueMap !== 'object') {
    return [];
  }

  const keys = getOrderedGradeKeys(valueMap);
  if (keys.length === 0) {
    return [];
  }

  return keys
    .map((gradeKey) => {
      const value = toNumber(valueMap[gradeKey]);
      if (options.currency) {
        return {
          label: formatGradeLabel(gradeKey),
          value: `BWP ${formatAmount(value)}`,
        };
      }
      if (options.litres) {
        return {
          label: formatGradeLabel(gradeKey),
          value: `${formatAmount(value)} L`,
        };
      }
      return {
        label: formatGradeLabel(gradeKey),
        value: formatAmount(value),
      };
    });
};

const formatOpeningClosingEntries = (valueMap) => {
  if (!valueMap || typeof valueMap !== 'object') {
    return [];
  }

  const keys = getOrderedGradeKeys(valueMap);
  if (keys.length === 0) {
    return [];
  }

  return keys
    .map((gradeKey) => {
      const openingVol = toNumber(valueMap?.[gradeKey]?.opening_vol);
      const closingVol = toNumber(valueMap?.[gradeKey]?.closing_vol);
      return {
        label: formatGradeLabel(gradeKey),
        value: `Open ${formatAmount(openingVol)} L • Close ${formatAmount(closingVol)} L`,
      };
    });
};

const SUMMARY_CARD_CONFIG = [
  { key: 'total_batches', label: 'Total Batches', icon: Inventory2Outlined, isCurrency: false },
  { key: 'total_actual', label: 'Total Actual', icon: AccountBalanceWalletOutlined, isCurrency: true },
  { key: 'total_theoretical', label: 'Total Theoretical', icon: CalculateOutlined, isCurrency: true },
  { key: 'total_variance', label: 'Total Variance', icon: CompareArrowsOutlined, isCurrency: true },
  { key: 'absolute_variance', label: 'Absolute Variance', icon: ErrorOutlineOutlined, isCurrency: true },
  { key: 'max_positive_variance', label: 'Max Positive', icon: TrendingUpOutlined, isCurrency: true },
  { key: 'max_negative_variance', label: 'Max Negative', icon: TrendingDownOutlined, isCurrency: true },
];

const BATCH_BREAKDOWN_CARD_CONFIG = [
  { key: 'total_batches', label: 'Total Batches', icon: Inventory2Outlined, isCurrency: false },
  { key: 'total_customers', label: 'Total Customers', icon: PeopleOutlineOutlined, isCurrency: false },
  { key: 'total_fuel_sales_value', label: 'Total Fuel Sales Value', icon: AccountBalanceWalletOutlined, isCurrency: true },
  { key: 'total_fuel_volume', label: 'Total Fuel Volume', icon: LocalGasStationOutlined, isLitres: true },
];

const CASHIER_EXCEPTION_CARD_CONFIG = [
  { key: 'total_cashiers', label: 'Total Cashiers', icon: PeopleOutlineOutlined, isCurrency: false },
  { key: 'total_nett_takings', label: 'Total Nett Takings', icon: AccountBalanceWalletOutlined, isCurrency: true },
  { key: 'total_batches', label: 'Total Batches', icon: Inventory2Outlined, isCurrency: false },
  { key: 'variance_volume', label: 'Variance Volume', icon: CompareArrowsOutlined, isLitres: true },
  { key: 'offline_quantity', label: 'Offline Quantity', icon: ErrorOutlineOutlined, isCurrency: false },
  { key: 'pump_errors_quantity', label: 'Pump Errors Quantity', icon: CalculateOutlined, isCurrency: false },
];

const FUEL_SALES_CONTROL_CARD_CONFIG = [
  {
    key: 'sales_amount',
    label: 'Sales Amount',
    icon: AccountBalanceWalletOutlined,
    formatType: 'fuel_currency',
    isMultiline: true,
  },
  {
    key: 'pump_vol_sales',
    label: 'Pump Vol Sales',
    icon: LocalGasStationOutlined,
    formatType: 'fuel_litres',
    isMultiline: true,
  },
  {
    key: 'tank_vol_sales',
    label: 'Tank Vol Sales',
    icon: StraightenOutlined,
    formatType: 'fuel_litres',
    isMultiline: true,
  },
  {
    key: 'opening_closing_vol',
    label: 'Opening / Closing Vol',
    icon: CompareArrowsOutlined,
    formatType: 'opening_closing',
    isMultiline: true,
  },
];

const PUMP_VARIANCE_CARD_CONFIG = [
  { key: 'total_pumps', label: 'Total Pumps', icon: LocalGasStationOutlined, isCurrency: false },
  {
    key: 'total_calculated_sales_volume',
    label: 'Calculated Sales Vol',
    icon: StraightenOutlined,
    isAmount: true,
  },
  {
    key: 'total_actual_sales_volume',
    label: 'Actual Sales Vol',
    icon: AccountBalanceWalletOutlined,
    isAmount: true,
  },
  {
    key: 'total_volume_variance',
    label: 'Volume Variance',
    icon: CompareArrowsOutlined,
    isAmount: true,
  },
];

const TABLE_TABS = [
  { key: 'batch_breakdown', label: 'Batch Breakdown' },
  { key: 'cashier_exceptions', label: 'Cashier Exceptions' },
  { key: 'category_sales', label: 'Category Sales' },
  { key: 'fuel_sales_control', label: 'Fuel Sales Control' },
  { key: 'pump_variance', label: 'Pump Variance' },
  { key: 'orders_created', label: 'Orders Created' },
];

const DEFAULT_LOGISTICS_TAB = TABLE_TABS[0].key;
const LOGISTICS_ACTIVE_TAB_STORAGE_KEY = 'depot_logistics_active_tab';

export default function DepotLogisticsPage() {
  const { showSnackbar } = useSnackbar();
  const showDepotExecutiveActions = isAuthorizedRole(['DEPOT-EXECUTIVE-C1']);
  const userInfo = useMemo(() => getUserInfo() || {}, []);
  const hasDepotRole = (userInfo?.realm_access?.roles || []).some((role) => role?.startsWith('DEPOT-'));
  const [depots, setDepots] = useState([]);
  const [selectedDepot, setSelectedDepot] = useState(ALL_DEPOTS_VALUE);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quickActionModalState, setQuickActionModalState] = useState({
    tanks: false,
    buy: false,
    addClient: false,
    addProduct: false,
  });

  const [summaryCards, setSummaryCards] = useState({});
  const [batchBreakdownCards, setBatchBreakdownCards] = useState({});
  const [cashierExceptionCards, setCashierExceptionCards] = useState({});
  const [fuelSalesControlCards, setFuelSalesControlCards] = useState({});
  const [pumpVarianceSummaryCards, setPumpVarianceSummaryCards] = useState({});
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_LOGISTICS_TAB;
    }

    const savedTab = window.localStorage.getItem(LOGISTICS_ACTIVE_TAB_STORAGE_KEY);
    const isValidSavedTab = TABLE_TABS.some((tab) => tab.key === savedTab);
    return isValidSavedTab ? savedTab : DEFAULT_LOGISTICS_TAB;
  });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [batchRows, setBatchRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [batchSummary, setBatchSummary] = useState({ total_batches: 0, net_variance: 0, absolute_variance: 0 });
  const [cashierRows, setCashierRows] = useState([]);
  const [cashierTotalRows, setCashierTotalRows] = useState(0);
  const [cashierPage, setCashierPage] = useState(1);
  const [cashierRowsPerPage, setCashierRowsPerPage] = useState(15);
  const [cashierLoading, setCashierLoading] = useState(false);
  const [expandedCashierBatchId, setExpandedCashierBatchId] = useState(null);
  const [cashierBatchDetailsById, setCashierBatchDetailsById] = useState({});
  const [cashierBatchDetailsLoadingById, setCashierBatchDetailsLoadingById] = useState({});
  const [cashierBatchDetailsErrorById, setCashierBatchDetailsErrorById] = useState({});
  const [categoryRows, setCategoryRows] = useState([]);
  const [categoryTotalRows, setCategoryTotalRows] = useState(0);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(10);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [fuelControlRows, setFuelControlRows] = useState([]);
  const [fuelControlTotalRows, setFuelControlTotalRows] = useState(0);
  const [fuelControlPage, setFuelControlPage] = useState(1);
  const [fuelControlRowsPerPage, setFuelControlRowsPerPage] = useState(10);
  const [fuelControlLoading, setFuelControlLoading] = useState(false);
  const [expandedFuelControlBatchId, setExpandedFuelControlBatchId] = useState(null);
  const [fuelControlDetailRowsByBatchId, setFuelControlDetailRowsByBatchId] = useState({});
  const [fuelControlDetailLoadingByBatchId, setFuelControlDetailLoadingByBatchId] = useState({});
  const [fuelControlDetailErrorByBatchId, setFuelControlDetailErrorByBatchId] = useState({});
  const [pumpVarianceRows, setPumpVarianceRows] = useState([]);
  const [pumpVariancePage, setPumpVariancePage] = useState(1);
  const [pumpVarianceRowsPerPage, setPumpVarianceRowsPerPage] = useState(15);
  const [pumpVarianceTotalRecords, setPumpVarianceTotalRecords] = useState(0);
  const [pumpVarianceLoading, setPumpVarianceLoading] = useState(false);
  const [expandedPumpVarianceBatchId, setExpandedPumpVarianceBatchId] = useState(null);
  const [pumpVarianceDetailRowsByBatchId, setPumpVarianceDetailRowsByBatchId] = useState({});
  const [pumpVarianceDetailLoadingByBatchId, setPumpVarianceDetailLoadingByBatchId] = useState({});
  const [pumpVarianceDetailErrorByBatchId, setPumpVarianceDetailErrorByBatchId] = useState({});
  const [ordersUpdateAllOders, setOrdersUpdateAllOders] = useState(false);
  const [openBatchModal, setOpenBatchModal] = useState(false);
  const [batchDetailLoading, setBatchDetailLoading] = useState(false);
  const [batchDetailError, setBatchDetailError] = useState('');
  const [selectedBatchDetail, setSelectedBatchDetail] = useState(null);
  const [openInspectBatchModal, setOpenInspectBatchModal] = useState(false);
  const [inspectBatchLoading, setInspectBatchLoading] = useState(false);
  const [inspectBatchError, setInspectBatchError] = useState('');
  const [inspectBatchData, setInspectBatchData] = useState(null);
  const [inspectBatchRequestMessage, setInspectBatchRequestMessage] = useState('');

  const quickActionOrderLimits = useMemo(
    () => ({
      volume: {
        min: 1,
        max: 9999999999,
      },
    }),
    [],
  );

  const depotSupplierScopeId = useMemo(() => {
    if (userInfo?.company_id) {
      return userInfo.company_id;
    }

    if (selectedDepot && selectedDepot !== ALL_DEPOTS_VALUE) {
      return selectedDepot;
    }

    return '';
  }, [selectedDepot, userInfo]);

  const depotRoleSupplierEndpoint = useMemo(() => {
    if (!hasDepotRole || !depotSupplierScopeId) {
      return null;
    }

    return `depots/suppliers?depot_id=${depotSupplierScopeId}`;
  }, [depotSupplierScopeId, hasDepotRole]);

  const setQuickActionModal = useCallback((modalName, openState) => {
    if (!modalName) {
      return;
    }

    setQuickActionModalState((previous) => ({
      ...previous,
      [modalName]: Boolean(openState),
    }));
  }, []);

  const toggleQuickActionModalState = useCallback((modalName, openState) => {
    if (!modalName) {
      return;
    }

    const modalKeyMap = {
      buy: 'buy',
      intransfer: 'tanks',
      transporter: 'addClient',
      product: 'addProduct',
    };
    const resolvedModalKey = modalKeyMap[modalName] || modalName;

    setQuickActionModalState((previous) => ({
      ...previous,
      [resolvedModalKey]: Boolean(openState),
    }));
  }, []);

  const handleAddDepotClick = useCallback(() => {
    showSnackbar({
      message: 'Add Depot will be enabled once the create-depot workflow is available.',
    });
  }, [showSnackbar]);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedDepot !== ALL_DEPOTS_VALUE) {
      params.append('depot_id', selectedDepot);
    }
    if (startDate) {
      params.append('time_start', startDate);
    }
    if (endDate) {
      params.append('time_end', endDate);
    }
    return params;
  }, [selectedDepot, startDate, endDate]);

  const fetchDepots = useCallback(async () => {
    try {
      const response = await API.get('depots/depots');
      const depotsData = Array.isArray(response.data) ? response.data : response.data?.depots || response.data?.data || [];
      const normalizedDepots = depotsData.map((depot) => ({
        ...depot,
        depot_id: depot?.depot_id || depot?.id,
        depot_name: depot?.depot_name || depot?.name,
      }));
      setDepots(normalizedDepots);
    } catch (error) {
      console.error('Error fetching depots for logistics tab:', error);
      setDepots([]);
    }
  }, []);

  const fetchSummaryCards = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const queryString = params.toString();
      const response = await API.get(`depots/cash-variance/summary${queryString ? `?${queryString}` : ''}`);
      setSummaryCards(response?.data?.cards || {});
    } catch (error) {
      console.error('Error fetching logistics summary cards:', error);
      setSummaryCards({});
    }
  }, [buildFilterParams]);

  const fetchBatchBreakdownCards = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedDepot !== ALL_DEPOTS_VALUE) {
        params.append('depot_id', selectedDepot);
      }
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }
      const queryString = params.toString();
      const response = await API.get(`depots/batch_breakdown/cards${queryString ? `?${queryString}` : ''}`);
      setBatchBreakdownCards(response?.data?.cards || {});
    } catch (error) {
      console.error('Error fetching batch breakdown cards:', error);
      setBatchBreakdownCards({});
    }
  }, [selectedDepot, startDate, endDate]);

  const fetchCashierExceptionCards = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const queryString = params.toString();
      const response = await API.get(`depots/cashier-exception/cards${queryString ? `?${queryString}` : ''}`);
      setCashierExceptionCards(response?.data?.cards || {});
    } catch (error) {
      console.error('Error fetching cashier exception cards:', error);
      setCashierExceptionCards({});
    }
  }, [buildFilterParams]);

  const fetchPumpVarianceSummaryCards = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const queryString = params.toString();
      const response = await API.get(`depots/pump-variance/summary${queryString ? `?${queryString}` : ''}`);
      setPumpVarianceSummaryCards(response?.data?.cards || {});
    } catch (error) {
      console.error('Error fetching pump variance summary cards:', error);
      setPumpVarianceSummaryCards({});
    }
  }, [buildFilterParams]);

  const fetchBatchBreakdownTable = useCallback(async () => {
    try {
      const params = buildFilterParams();
      params.append('page', String(page));
      params.append('records', String(rowsPerPage));
      const response = await API.get(`depots/cash-variance/batch-breakdown?${params.toString()}`);
      setBatchRows(response?.data?.data || []);
      setTotalRecords(response?.data?.total_rows || response?.data?.total_records || 0);
      setBatchSummary(
        response?.data?.summary || {
          total_batches: 0,
          net_variance: 0,
          absolute_variance: 0,
        },
      );
    } catch (error) {
      console.error('Error fetching cash variance batch breakdown table:', error);
      setBatchRows([]);
      setTotalRecords(0);
      setBatchSummary({ total_batches: 0, net_variance: 0, absolute_variance: 0 });
    }
  }, [buildFilterParams, page, rowsPerPage]);

  const fetchCashierExceptionsTable = useCallback(async () => {
    setCashierLoading(true);
    try {
      const params = buildFilterParams();
      params.append('page', String(cashierPage));
      params.append('records', String(cashierRowsPerPage));
      const response = await API.get(`depots/cashier-category-sales-summary?${params.toString()}`);

      const rows = response?.data?.rows || response?.data?.data || [];
      setCashierRows(Array.isArray(rows) ? rows : []);
      setCashierTotalRows(
        response?.data?.total_rows || response?.data?.total_records || response?.data?.total_objects_returned || 0,
      );
    } catch (error) {
      console.error('Error fetching cashier exceptions table:', error);
      setCashierRows([]);
      setCashierTotalRows(0);
    } finally {
      setCashierLoading(false);
    }
  }, [buildFilterParams, cashierPage, cashierRowsPerPage]);

  const fetchCashierBatchDetails = useCallback(
    async (batchId) => {
      if (!batchId) {
        return;
      }

      setCashierBatchDetailsLoadingById((previous) => ({ ...previous, [batchId]: true }));
      setCashierBatchDetailsErrorById((previous) => ({ ...previous, [batchId]: '' }));

      try {
        const params = buildFilterParams();
        params.append('batch_id', String(batchId));
        params.append('page', '1');
        params.append('records', '10');
        const response = await API.get(`depots/cashier-exception/trend?${params.toString()}`);
        const rows = response?.data?.rows || response?.data?.data || [];
        setCashierBatchDetailsById((previous) => ({
          ...previous,
          [batchId]: Array.isArray(rows) ? rows : [],
        }));
      } catch (error) {
        console.error('Error fetching cashier exception detail rows:', error);
        setCashierBatchDetailsById((previous) => ({ ...previous, [batchId]: [] }));
        setCashierBatchDetailsErrorById((previous) => ({
          ...previous,
          [batchId]: 'Failed to load cashier details for this batch.',
        }));
      } finally {
        setCashierBatchDetailsLoadingById((previous) => ({ ...previous, [batchId]: false }));
      }
    },
    [buildFilterParams],
  );

  const fetchCategorySalesTable = useCallback(async () => {
    setCategoryLoading(true);
    try {
      const params = buildFilterParams();
      params.append('page', String(categoryPage));
      params.append('records', String(categoryRowsPerPage));
      const response = await API.get(`depots/category-sales/trend?${params.toString()}`);

      const rows = response?.data?.rows || response?.data?.data || [];
      setCategoryRows(Array.isArray(rows) ? rows : []);
      setCategoryTotalRows(
        response?.data?.total_rows ||
          response?.data?.total_objects_returned ||
          response?.data?.total_records ||
          0,
      );
    } catch (error) {
      console.error('Error fetching category sales table:', error);
      setCategoryRows([]);
      setCategoryTotalRows(0);
    } finally {
      setCategoryLoading(false);
    }
  }, [buildFilterParams, categoryPage, categoryRowsPerPage]);

  const fetchFuelSalesControlTable = useCallback(async () => {
    setFuelControlLoading(true);
    try {
      const params = buildFilterParams();
      params.append('page', String(fuelControlPage));
      params.append('records', String(fuelControlRowsPerPage));

      const response = await API.get(`depots/fuel-sales-control/trend?${params.toString()}`);
      let rows = response?.data?.rows || response?.data?.data || [];

      // Backward compatibility for older payloads that returned grouped series.
      if ((!Array.isArray(rows) || rows.length === 0) && Array.isArray(response?.data?.series)) {
        const flattenedRows = [];
        response.data.series.forEach((seriesItem) => {
          const gradeName = formatLabel(seriesItem?.name || '');
          (seriesItem?.data || []).forEach((point) => {
            flattenedRows.push({
              ...point,
              grade_name: gradeName || '-',
            });
          });
        });
        rows = flattenedRows;
      }

      setFuelControlRows(Array.isArray(rows) ? rows : []);
      setFuelControlTotalRows(
        toNumber(
          response?.data?.total_rows ||
            response?.data?.total_records ||
            response?.data?.total_objects_returned ||
            rows.length,
        ),
      );
    } catch (error) {
      console.error('Error fetching fuel sales control table:', error);
      setFuelControlRows([]);
      setFuelControlTotalRows(0);
    } finally {
      setFuelControlLoading(false);
    }
  }, [buildFilterParams, fuelControlPage, fuelControlRowsPerPage]);

  const fetchFuelControlBatchDetails = useCallback(
    async (batchId) => {
      if (!batchId) {
        return;
      }

      setFuelControlDetailLoadingByBatchId((previous) => ({ ...previous, [batchId]: true }));
      setFuelControlDetailErrorByBatchId((previous) => ({ ...previous, [batchId]: '' }));

      try {
        const params = buildFilterParams();
        params.append('batch_id', String(batchId));
        params.append('page', '1');
        params.append('records', '15');

        const response = await API.get(`depots/fuel-sales-control/trend?${params.toString()}`);
        const rows = response?.data?.rows || response?.data?.data || [];
        setFuelControlDetailRowsByBatchId((previous) => ({
          ...previous,
          [batchId]: Array.isArray(rows) ? rows : [],
        }));
      } catch (error) {
        console.error('Error fetching fuel sales control detail rows:', error);
        setFuelControlDetailRowsByBatchId((previous) => ({ ...previous, [batchId]: [] }));
        setFuelControlDetailErrorByBatchId((previous) => ({
          ...previous,
          [batchId]: 'Failed to load fuel sales control details for this batch.',
        }));
      } finally {
        setFuelControlDetailLoadingByBatchId((previous) => ({ ...previous, [batchId]: false }));
      }
    },
    [buildFilterParams],
  );

  const fetchFuelSalesControlCards = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const queryString = params.toString();
      const response = await API.get(`depots/fuel-sales-control/cards${queryString ? `?${queryString}` : ''}`);
      setFuelSalesControlCards(response?.data?.cards || {});
    } catch (error) {
      console.error('Error fetching fuel sales control cards:', error);
      setFuelSalesControlCards({});
    }
  }, [buildFilterParams]);

  const fetchPumpVarianceTable = useCallback(async () => {
    setPumpVarianceLoading(true);
    try {
      const params = buildFilterParams();
      params.append('page', String(pumpVariancePage));
      params.append('records', String(pumpVarianceRowsPerPage));

      const response = await API.get(`depots/pump-variance/table?${params.toString()}`);
      const rows = response?.data?.rows || response?.data?.data || [];

      setPumpVarianceRows(Array.isArray(rows) ? rows : []);
      setPumpVarianceTotalRecords(
        toNumber(response?.data?.total_rows || response?.data?.total_records || response?.data?.record || 0),
      );
    } catch (error) {
      console.error('Error fetching pump variance table:', error);
      setPumpVarianceRows([]);
      setPumpVarianceTotalRecords(0);
    } finally {
      setPumpVarianceLoading(false);
    }
  }, [buildFilterParams, pumpVariancePage, pumpVarianceRowsPerPage]);

  const fetchPumpVarianceBatchDetails = useCallback(
    async (batchId) => {
      if (!batchId) {
        return;
      }

      setPumpVarianceDetailLoadingByBatchId((previous) => ({ ...previous, [batchId]: true }));
      setPumpVarianceDetailErrorByBatchId((previous) => ({ ...previous, [batchId]: '' }));

      try {
        const params = buildFilterParams();
        params.append('batch_id', String(batchId));
        params.append('page', '1');
        params.append('records', '15');

        const response = await API.get(`depots/pump-variance/table?${params.toString()}`);
        const rows = response?.data?.rows || response?.data?.data || [];
        setPumpVarianceDetailRowsByBatchId((previous) => ({
          ...previous,
          [batchId]: Array.isArray(rows) ? rows : [],
        }));
      } catch (error) {
        console.error('Error fetching pump variance detail rows:', error);
        setPumpVarianceDetailRowsByBatchId((previous) => ({ ...previous, [batchId]: [] }));
        setPumpVarianceDetailErrorByBatchId((previous) => ({
          ...previous,
          [batchId]: 'Failed to load pump variance details for this batch.',
        }));
      } finally {
        setPumpVarianceDetailLoadingByBatchId((previous) => ({ ...previous, [batchId]: false }));
      }
    },
    [buildFilterParams],
  );

  useEffect(() => {
    fetchDepots();
  }, [fetchDepots]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(LOGISTICS_ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
    setCashierPage(1);
    setCategoryPage(1);
    setFuelControlPage(1);
    setPumpVariancePage(1);
    setExpandedCashierBatchId(null);
    setCashierBatchDetailsById({});
    setCashierBatchDetailsLoadingById({});
    setCashierBatchDetailsErrorById({});
    setExpandedFuelControlBatchId(null);
    setFuelControlDetailRowsByBatchId({});
    setFuelControlDetailLoadingByBatchId({});
    setFuelControlDetailErrorByBatchId({});
    setExpandedPumpVarianceBatchId(null);
    setPumpVarianceDetailRowsByBatchId({});
    setPumpVarianceDetailLoadingByBatchId({});
    setPumpVarianceDetailErrorByBatchId({});
  }, [selectedDepot, startDate, endDate]);

  useEffect(() => {
    setExpandedFuelControlBatchId(null);
  }, [fuelControlPage, fuelControlRowsPerPage]);

  useEffect(() => {
    setExpandedPumpVarianceBatchId(null);
  }, [pumpVariancePage, pumpVarianceRowsPerPage]);

  useEffect(() => {
    fetchSummaryCards();
    fetchBatchBreakdownTable();
  }, [fetchSummaryCards, fetchBatchBreakdownTable]);

  useEffect(() => {
    if (activeTab === 'cashier_exceptions') {
      fetchCashierExceptionCards();
      fetchCashierExceptionsTable();
    }
    if (activeTab === 'batch_breakdown') {
      fetchBatchBreakdownCards();
    }
    if (activeTab === 'category_sales') {
      fetchCategorySalesTable();
    }
    if (activeTab === 'fuel_sales_control') {
      fetchFuelSalesControlCards();
      fetchFuelSalesControlTable();
    }
    if (activeTab === 'pump_variance') {
      fetchPumpVarianceSummaryCards();
      fetchPumpVarianceTable();
    }
  }, [
    activeTab,
    fetchBatchBreakdownCards,
    fetchCashierExceptionCards,
    fetchCashierExceptionsTable,
    fetchCategorySalesTable,
    fetchFuelSalesControlCards,
    fetchFuelSalesControlTable,
    fetchPumpVarianceSummaryCards,
    fetchPumpVarianceTable,
  ]);

  const cardData = useMemo(
    () => {
      let cardConfig = SUMMARY_CARD_CONFIG;
      let cardSource = summaryCards;

      if (activeTab === 'batch_breakdown') {
        cardConfig = BATCH_BREAKDOWN_CARD_CONFIG;
        cardSource = batchBreakdownCards;
      } else if (activeTab === 'cashier_exceptions') {
        cardConfig = CASHIER_EXCEPTION_CARD_CONFIG;
        cardSource = cashierExceptionCards;
      } else if (activeTab === 'fuel_sales_control') {
        cardConfig = FUEL_SALES_CONTROL_CARD_CONFIG;
        cardSource = fuelSalesControlCards;
      } else if (activeTab === 'pump_variance') {
        cardConfig = PUMP_VARIANCE_CARD_CONFIG;
        cardSource = pumpVarianceSummaryCards;
      }

      return cardConfig.map((card) => {
        const value = cardSource?.[card.key];
        let displayValue = integerFormatter.format(toNumber(value));
        let displayEntries = [];
        if (card.formatType === 'fuel_currency') {
          displayEntries = formatFuelMetricEntries(value, { currency: true });
        } else if (card.formatType === 'fuel_litres') {
          displayEntries = formatFuelMetricEntries(value, { litres: true });
        } else if (card.formatType === 'opening_closing') {
          displayEntries = formatOpeningClosingEntries(value);
        } else if (card.isCurrency) {
          displayValue = formatCurrency(value);
        } else if (card.isAmount) {
          displayValue = formatAmount(value);
        } else if (card.isLitres) {
          displayValue = `${formatAmount(value)} L`;
        }

        if (card.isMultiline) {
          displayValue = '-';
        }

        return {
          ...card,
          displayValue,
          displayEntries,
        };
      });
    },
    [
      activeTab,
      batchBreakdownCards,
      cashierExceptionCards,
      fuelSalesControlCards,
      pumpVarianceSummaryCards,
      summaryCards,
    ],
  );

  const depotNameById = useMemo(() => {
    const mapping = {};
    depots.forEach((depot) => {
      mapping[String(depot.depot_id)] = depot.depot_name;
    });
    return mapping;
  }, [depots]);

  const getDepotLabel = useCallback(
    (depotId) => depotNameById[String(depotId)] || `Depot ${depotId}`,
    [depotNameById],
  );

  const fetchBatchDetail = useCallback(
    async (batchId) => {
      setBatchDetailLoading(true);
      setBatchDetailError('');
      try {
        const params = new URLSearchParams();
        params.append('batch_id', String(batchId));
        if (startDate) {
          params.append('time_start', startDate);
        }
        if (endDate) {
          params.append('time_end', endDate);
        }
        const response = await API.get(`depots/cash-variance/batch-breakdown?${params.toString()}`);
        setSelectedBatchDetail(response?.data || null);
      } catch (error) {
        console.error('Error fetching batch detail:', error);
        setSelectedBatchDetail(null);
        setBatchDetailError('Failed to load batch details.');
      } finally {
        setBatchDetailLoading(false);
      }
    },
    [startDate, endDate],
  );

  const fetchInspectBatchData = useCallback(async (batchId) => {
    setInspectBatchLoading(true);
    setInspectBatchError('');
    try {
      const params = new URLSearchParams();
      params.append('batch_id', String(batchId));
      params.append('include_content', 'true');
      const response = await API.get(`depots/processed-emails/raw-file?${params.toString()}`);
      setInspectBatchData(response?.data || null);
    } catch (error) {
      console.error('Error fetching inspect batch raw file:', error);
      setInspectBatchData(null);
      setInspectBatchError('Failed to load raw file for this batch.');
    } finally {
      setInspectBatchLoading(false);
    }
  }, []);

  const handleBatchRowClick = (batchId) => {
    if (!batchId) {
      return;
    }
    setOpenBatchModal(true);
    setSelectedBatchDetail(null);
    fetchBatchDetail(batchId);
  };

  const handleCashierSummaryRowClick = useCallback(
    (row) => {
      const batchId = toNumber(row?.id || row?.batch_id);
      if (!batchId) {
        return;
      }

      if (expandedCashierBatchId === batchId) {
        setExpandedCashierBatchId(null);
        return;
      }

      setExpandedCashierBatchId(batchId);
      if (!cashierBatchDetailsById[batchId]) {
        fetchCashierBatchDetails(batchId);
      }
    },
    [cashierBatchDetailsById, expandedCashierBatchId, fetchCashierBatchDetails],
  );

  const handlePumpVarianceSummaryRowClick = useCallback(
    (row) => {
      const batchId = toNumber(row?.batch_id || row?.id);
      if (!batchId) {
        return;
      }

      if (expandedPumpVarianceBatchId === batchId) {
        setExpandedPumpVarianceBatchId(null);
        return;
      }

      setExpandedPumpVarianceBatchId(batchId);
      if (!pumpVarianceDetailRowsByBatchId[batchId]) {
        fetchPumpVarianceBatchDetails(batchId);
      }
    },
    [expandedPumpVarianceBatchId, fetchPumpVarianceBatchDetails, pumpVarianceDetailRowsByBatchId],
  );

  const handleFuelControlSummaryRowClick = useCallback(
    (row) => {
      const batchId = toNumber(row?.batch_id || row?.id);
      if (!batchId) {
        return;
      }

      if (expandedFuelControlBatchId === batchId) {
        setExpandedFuelControlBatchId(null);
        return;
      }

      setExpandedFuelControlBatchId(batchId);
      if (!fuelControlDetailRowsByBatchId[batchId]) {
        fetchFuelControlBatchDetails(batchId);
      }
    },
    [expandedFuelControlBatchId, fetchFuelControlBatchDetails, fuelControlDetailRowsByBatchId],
  );

  const handleInspectBatchClick = () => {
    const batchId = selectedBatchDetail?.batch_id;
    if (!batchId) {
      return;
    }
    setOpenInspectBatchModal(true);
    setInspectBatchData(null);
    setInspectBatchRequestMessage('');
    fetchInspectBatchData(batchId);
  };

  const batchDetailRows = useMemo(
    () => (Array.isArray(selectedBatchDetail?.data) ? selectedBatchDetail.data : []),
    [selectedBatchDetail],
  );

  const batchDetailGroupSummary = useMemo(() => {
    const grouped = {};
    batchDetailRows.forEach((row) => {
      const key = row?.tender_group || 'UNKNOWN';
      if (!grouped[key]) {
        grouped[key] = { tender_group: key, actual: 0, theoretical: 0, variance: 0 };
      }
      grouped[key].actual += toNumber(row.actual);
      grouped[key].theoretical += toNumber(row.theoretical);
      grouped[key].variance += toNumber(row.variance);
    });
    return Object.values(grouped);
  }, [batchDetailRows]);

  const inspectBatchMetadataCards = useMemo(() => {
    const metadata = inspectBatchData?.metadata || {};
    const file = inspectBatchData?.file || {};
    const rawFile = inspectBatchData?.raw_file || {};

    return [
      { label: 'Depot', value: metadata?.depot_name || '-' },
      { label: 'Batch #', value: metadata?.batch_number || '-' },
      { label: 'Batch Date', value: metadata?.batch_date || '-' },
      { label: 'Sender', value: metadata?.sender_email || metadata?.sender || '-' },
      { label: 'Status', value: metadata?.status || '-' },
      { label: 'RPT File', value: metadata?.filename || file?.object_name || '-' },
      { label: 'File Size', value: file?.size_bytes ? `${integerFormatter.format(toNumber(file.size_bytes))} bytes` : '-' },
      { label: 'Line Count', value: integerFormatter.format(toNumber(rawFile?.line_count)) },
    ];
  }, [inspectBatchData]);

  const inspectBatchRawContent =
    inspectBatchData?.raw_file?.content || inspectBatchData?.raw_file?.preview || '';

  const isOrderTabActive = activeTab === 'orders_created';

  const tabRowCountByKey = useMemo(
    () => ({
      orders_created: 0,
      batch_breakdown: totalRecords,
      cashier_exceptions: cashierTotalRows,
      category_sales: categoryTotalRows,
      fuel_sales_control: fuelControlTotalRows,
      pump_variance: pumpVarianceTotalRecords,
    }),
    [totalRecords, cashierTotalRows, categoryTotalRows, fuelControlTotalRows, pumpVarianceTotalRecords],
  );

  return (
    <>
      <Helmet>
        <title> Logistics </title>
      </Helmet>

      <Container maxWidth={false}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', lg: 'center' },
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ mb: 1 }}>
              Logistics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Depot cash variance monitoring with all-depot and per-depot filtering.
            </Typography>
          </Box>

          {showDepotExecutiveActions && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PropaneTankOutlined fontSize="small" />}
                onClick={() => setQuickActionModal('tanks', true)}
                disabled
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Tanks
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddShoppingCartOutlined fontSize="small" />}
                onClick={() => setQuickActionModal('buy', true)}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Create Order
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<GroupAddOutlined fontSize="small" />}
                onClick={() => setQuickActionModal('addClient', true)}
                disabled
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Add Client
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Inventory2Outlined fontSize="small" />}
                onClick={() => setQuickActionModal('addProduct', true)}
                disabled
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Add Product
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddBusinessOutlined fontSize="small" />}
                onClick={handleAddDepotClick}
                disabled
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Add Depot
              </Button>
            </Stack>
          )}
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            select
            size="small"
            label="Depot"
            value={selectedDepot}
            onChange={(event) => setSelectedDepot(event.target.value)}
            sx={{ minWidth: { xs: '100%', md: 220 } }}
          >
            <MenuItem value={ALL_DEPOTS_VALUE}>All Depots</MenuItem>
            {depots.map((depot) => (
              <MenuItem key={`depot-filter-${depot.depot_id}`} value={String(depot.depot_id)}>
                {depot.depot_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            size="small"
            label="Start Date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', md: 170 } }}
          />
          <TextField
            type="date"
            size="small"
            label="End Date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
            sx={{ minWidth: { xs: '100%', md: 170 } }}
          />
        </Stack>

        {!isOrderTabActive && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {cardData.map((card, index) => {
              const Icon = card.icon;
              const cardTone = getCardTone(index);
              const multiLineValues = Array.isArray(card.displayEntries) ? card.displayEntries : [];

              return (
                <Grid
                  size={card.isMultiline ? { xs: 12, sm: 6, md: 6, lg: 6 } : { xs: 12, sm: 6, md: 4, lg: 3 }}
                  key={`summary-card-${card.key}`}
                >
                  <Card
                    sx={{
                      height: '100%',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderLeft: `4px solid ${cardTone.accent}`,
                      background: `linear-gradient(145deg, ${cardTone.surface} 0%, #ffffff 62%)`,
                      boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
                      transition: 'transform 180ms ease, box-shadow 180ms ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.2 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1.5,
                            backgroundColor: cardTone.surface,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: cardTone.accent,
                            flexShrink: 0,
                          }}
                        >
                          <Icon fontSize="small" />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontWeight: 700 }}
                        >
                          {card.label}
                        </Typography>
                      </Stack>

                      {card.isMultiline ? (
                        <Grid container spacing={1}>
                          {(multiLineValues.length
                            ? multiLineValues
                            : [{ label: 'No Data', value: '-' }]).map((entry, lineIndex) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={`card-line-${card.key}-${lineIndex}`}>
                              <Box
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                  p: 1,
                                  backgroundColor: '#ffffff',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}
                                >
                                  {entry.label}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35, color: '#1f2937' }}>
                                  {entry.value}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#0f172a' }}>
                          {card.displayValue}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Paper
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            overflow: 'hidden',
            boxShadow: '0 10px 26px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1.2,
              backgroundColor: '#f8fafc',
            }}
          >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {TABLE_TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                const tabCount = toNumber(tabRowCountByKey[tab.key] || 0);

                return (
                  <Button
                    key={`logistics-tab-${tab.key}`}
                    size="small"
                    onClick={() => setActiveTab(tab.key)}
                    variant={isActive ? 'contained' : 'outlined'}
                    sx={{
                      borderRadius: 999,
                      textTransform: 'none',
                      px: 1,
                      py: 0.45,
                      fontWeight: 700,
                      borderColor: isActive ? '#2563eb' : '#cbd5e1',
                      color: isActive ? '#ffffff' : '#334155',
                      backgroundColor: isActive ? '#2563eb' : '#ffffff',
                      '&:hover': {
                        borderColor: '#2563eb',
                        backgroundColor: isActive ? '#1d4ed8' : '#eff6ff',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: isActive ? '#ffffff' : '#334155',
                        }}
                      >
                        {tab.label}
                      </Typography>
                      <Chip
                        size="small"
                        label={integerFormatter.format(tabCount)}
                        sx={{
                          height: 20,
                          borderRadius: 999,
                          fontWeight: 700,
                          backgroundColor: isActive ? 'rgba(255,255,255,0.24)' : '#e2e8f0',
                          color: isActive ? '#ffffff' : '#0f172a',
                        }}
                      />
                    </Stack>
                  </Button>
                );
              })}
            </Stack>
          </Box>

          {activeTab === 'orders_created' && (
            <Box sx={{ p: 2 }}>
              <AllOrders
                filterString=""
                orderCategory="all"
                updateAllOders={ordersUpdateAllOders}
                setUpdateAllOders={setOrdersUpdateAllOders}
                setUpdateStats={() => {}}
                enableDepotOrderActions={hasDepotRole}
              />
            </Box>
          )}

          {activeTab === 'batch_breakdown' && (
            <Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.2}
                sx={TABLE_SECTION_HEADER_SX}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Cash Variance Batch Breakdown
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    Total Batches: {integerFormatter.format(toNumber(batchSummary.total_batches))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Net Variance: {formatCurrency(batchSummary.net_variance)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Absolute Variance: {formatCurrency(batchSummary.absolute_variance)}
                  </Typography>
                </Stack>
              </Stack>

              <Box sx={{ ...TABLE_PANEL_SX }}>
                <Table size="small" sx={DATA_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Depot</TableCell>
                      <TableCell align="right">Cash In Drawer</TableCell>
                      <TableCell align="right">Lifts/Drops</TableCell>
                      <TableCell align="right">Actual</TableCell>
                      <TableCell align="right">Theoretical</TableCell>
                      <TableCell align="right">Variance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchRows.map((row, rowIndex) => (
                      <TableRow
                        key={`batch-breakdown-row-${row.batch_id}-${row.batch_number}`}
                        hover
                        onClick={() => handleBatchRowClick(row.batch_id)}
                        sx={getStripedRowSx(rowIndex, { clickable: true })}
                      >
                        <TableCell>{row.batch_number}</TableCell>
                        <TableCell>{row.batch_date || '-'}</TableCell>
                        <TableCell>{getDepotLabel(row.depot_id)}</TableCell>
                        <TableCell align="right">{formatAmount(row.cash_in_drawer)}</TableCell>
                        <TableCell align="right">{formatAmount(row.lifts_drops)}</TableCell>
                        <TableCell align="right">{formatAmount(row.actual)}</TableCell>
                        <TableCell align="right">{formatAmount(row.theoretical)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: toNumber(row.variance) < 0 ? 'error.main' : 'success.main', fontWeight: 700 }}
                        >
                          {formatAmount(row.variance)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {batchRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No batch breakdown data found for current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={totalRecords}
                page={page - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                sx={TABLE_PAGINATION_SX}
              />
            </Box>
          )}

          {activeTab === 'cashier_exceptions' && (
            <Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.2}
                sx={TABLE_SECTION_HEADER_SX}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Cashier Category Sales Summary
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Rows: {integerFormatter.format(toNumber(cashierTotalRows))}
                </Typography>
              </Stack>

              <Box sx={{ ...TABLE_PANEL_SX }}>
                <Table size="small" sx={DATA_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Depot</TableCell>
                      <TableCell align="right">Qty Sold</TableCell>
                      <TableCell align="right">Total Sales</TableCell>
                      <TableCell align="right">GP Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashierLoading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    )}

                    {!cashierLoading &&
                      cashierRows.map((row, rowIndex) => {
                        const batchId = toNumber(row?.id || row?.batch_id);
                        const isExpanded = expandedCashierBatchId === batchId;
                        const detailRows = cashierBatchDetailsById[batchId] || [];
                        const detailLoading = Boolean(cashierBatchDetailsLoadingById[batchId]);
                        const detailError = cashierBatchDetailsErrorById[batchId];

                        return (
                          <React.Fragment key={`cashier-summary-row-${batchId || row.batch_number}`}>
                            <TableRow
                              hover
                              onClick={() => handleCashierSummaryRowClick(row)}
                              sx={{
                                ...getStripedRowSx(rowIndex, { clickable: true }),
                                backgroundColor: isExpanded ? '#eaf2ff' : undefined,
                              }}
                            >
                              <TableCell sx={{ fontWeight: 700 }}>{row.batch_number || '-'}</TableCell>
                              <TableCell>{row.batch_date || '-'}</TableCell>
                              <TableCell>{getDepotLabel(row.depot_id)}</TableCell>
                              <TableCell align="right">{formatAmount(row.qty_sold)}</TableCell>
                              <TableCell align="right">{formatAmount(row.total_sales)}</TableCell>
                              <TableCell align="right">{formatAmount(row.gp_amount)}</TableCell>
                            </TableRow>

                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ py: 0, backgroundColor: '#f1f5f9', borderBottom: 0 }}>
                                  <Box sx={{ px: 1.5, py: 1.2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                                      Batch {row.batch_number || '-'} cashier exceptions
                                    </Typography>

                                    {detailLoading && (
                                      <Box sx={{ py: 1.2, display: 'flex', justifyContent: 'center' }}>
                                        <CircularProgress size={20} />
                                      </Box>
                                    )}

                                    {!detailLoading && detailError && (
                                      <Typography variant="body2" color="error.main">
                                        {detailError}
                                      </Typography>
                                    )}

                                    {!detailLoading && !detailError && (
                                      <Table
                                        size="small"
                                        sx={{
                                          ...DATA_TABLE_SX,
                                          minWidth: 560,
                                          backgroundColor: '#ffffff',
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          borderRadius: 1.5,
                                        }}
                                      >
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Cashier</TableCell>
                                            <TableCell align="right">Nett Takings</TableCell>
                                            <TableCell align="right">Variance Vol</TableCell>
                                            <TableCell align="right">Variance Amount</TableCell>
                                            <TableCell align="right">Pump Errors</TableCell>
                                            <TableCell align="right">Offline</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {detailRows.map((detailRow, detailIndex) => (
                                            <TableRow
                                              key={`cashier-detail-${batchId}-${detailRow.cashier_name}-${detailIndex}`}
                                              sx={getStripedRowSx(detailIndex, { disableHover: true })}
                                            >
                                              <TableCell>{detailRow.cashier_name || '-'}</TableCell>
                                              <TableCell align="right">{formatAmount(detailRow.nett_takings)}</TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  color: toNumber(detailRow.variance_volume) < 0 ? 'error.main' : 'inherit',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {formatAmount(detailRow.variance_volume)}
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{
                                                  color: toNumber(detailRow.variance_amount) < 0 ? 'error.main' : 'inherit',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {formatAmount(detailRow.variance_amount)}
                                              </TableCell>
                                              <TableCell align="right">{integerFormatter.format(toNumber(detailRow.pump_errors_quantity))}</TableCell>
                                              <TableCell align="right">{integerFormatter.format(toNumber(detailRow.offline_quantity))}</TableCell>
                                            </TableRow>
                                          ))}

                                          {detailRows.length === 0 && (
                                            <TableRow>
                                              <TableCell colSpan={6} align="center">
                                                No cashier exception rows for this batch.
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}

                    {!cashierLoading && cashierRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No cashier summary data found for current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={cashierTotalRows}
                page={cashierPage - 1}
                onPageChange={(_, newPage) => setCashierPage(newPage + 1)}
                rowsPerPage={cashierRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setCashierRowsPerPage(parseInt(event.target.value, 10));
                  setCashierPage(1);
                }}
                rowsPerPageOptions={[15, 25, 50]}
                sx={TABLE_PAGINATION_SX}
              />
            </Box>
          )}

          {activeTab === 'category_sales' && (
            <Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.2}
                sx={TABLE_SECTION_HEADER_SX}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Category Sales Trend
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Rows: {integerFormatter.format(toNumber(categoryTotalRows))}
                </Typography>
              </Stack>

              <Box sx={{ ...TABLE_PANEL_SX }}>
                <Table size="small" sx={DATA_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Depot</TableCell>
                      <TableCell align="right">Qty Sold</TableCell>
                      <TableCell align="right">Cost of Sales</TableCell>
                      <TableCell align="right">Sales Value</TableCell>
                      <TableCell align="right">Total Sales</TableCell>
                      <TableCell align="right">GP Amount</TableCell>
                      <TableCell align="right">GP %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryLoading && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    )}

                    {!categoryLoading &&
                      categoryRows.map((row, rowIndex) => (
                        <TableRow
                          key={`category-row-${row.batch_id}-${row.category}-${row.batch_number}`}
                          hover
                          sx={getStripedRowSx(rowIndex)}
                        >
                          <TableCell>{row.batch_number || '-'}</TableCell>
                          <TableCell>{row.batch_date || '-'}</TableCell>
                          <TableCell>{getDepotLabel(row.depot_id)}</TableCell>
                          <TableCell align="right">{formatAmount(row.qty_sold)}</TableCell>
                          <TableCell align="right">{formatAmount(row.cost_of_sales)}</TableCell>
                          <TableCell align="right">{formatAmount(row.sales_value)}</TableCell>
                          <TableCell align="right">{formatAmount(row.total_sales)}</TableCell>
                          <TableCell align="right">{formatAmount(row.gp_amount)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: toNumber(row.gp_percentage) < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}
                          >
                            {toNumber(row.gp_percentage).toFixed(2)}%
                          </TableCell>
                        </TableRow>
                      ))}

                    {!categoryLoading && categoryRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No category sales data found for current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={categoryTotalRows}
                page={categoryPage - 1}
                onPageChange={(_, newPage) => setCategoryPage(newPage + 1)}
                rowsPerPage={categoryRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setCategoryRowsPerPage(parseInt(event.target.value, 10));
                  setCategoryPage(1);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                sx={TABLE_PAGINATION_SX}
              />
            </Box>
          )}

          {activeTab === 'fuel_sales_control' && (
            <Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.2}
                sx={TABLE_SECTION_HEADER_SX}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Fuel Sales Control Trend
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Rows: {integerFormatter.format(toNumber(fuelControlTotalRows))}
                </Typography>
              </Stack>

              <Box sx={{ ...TABLE_PANEL_SX }}>
                <Table size="small" sx={DATA_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Sales Amount</TableCell>
                      <TableCell align="right">Pump Vol Sales</TableCell>
                      <TableCell align="right">Tank Vol Sales</TableCell>
                      <TableCell align="right">Pump Vol Variance</TableCell>
                      <TableCell align="right">Opening Vol</TableCell>
                      <TableCell align="right">Closing Dip</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fuelControlLoading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    )}

                    {!fuelControlLoading &&
                      fuelControlRows.map((row, rowIndex) => {
                        const batchId = toNumber(row.batch_id || row.id);
                        const isExpanded = expandedFuelControlBatchId === batchId;
                        const detailRows = fuelControlDetailRowsByBatchId[batchId] || [];
                        const detailLoading = Boolean(fuelControlDetailLoadingByBatchId[batchId]);
                        const detailError = fuelControlDetailErrorByBatchId[batchId];

                        return (
                          <React.Fragment key={`fuel-control-summary-${batchId || row.batch_number}`}>
                            <TableRow
                              hover
                              onClick={() => handleFuelControlSummaryRowClick(row)}
                              sx={{
                                ...getStripedRowSx(rowIndex, { clickable: true }),
                                backgroundColor: isExpanded ? '#eaf2ff' : undefined,
                              }}
                            >
                              <TableCell sx={{ fontWeight: 700 }}>{row.batch_number || '-'}</TableCell>
                              <TableCell>{row.batch_date || '-'}</TableCell>
                              <TableCell align="right">{formatAmount(row.days_amount_sales)}</TableCell>
                              <TableCell align="right">{formatAmount(row.days_pump_vol_sales)}</TableCell>
                              <TableCell align="right">{formatAmount(row.days_tank_vol_sales)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: toNumber(row.days_pump_vol_variance) < 0 ? 'error.main' : 'success.main',
                                  fontWeight: 600,
                                }}
                              >
                                {formatAmount(row.days_pump_vol_variance)}
                              </TableCell>
                              <TableCell align="right">{formatAmount(row.opening_vol)}</TableCell>
                              <TableCell align="right">{formatAmount(row.closing_dip)}</TableCell>
                            </TableRow>

                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={8} sx={{ py: 0, backgroundColor: '#f1f5f9', borderBottom: 0 }}>
                                  <Box sx={{ px: 1.5, py: 1.2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                                      Batch {row.batch_number || '-'} fuel sales control details
                                    </Typography>

                                    {detailLoading && (
                                      <Box sx={{ py: 1.2, display: 'flex', justifyContent: 'center' }}>
                                        <CircularProgress size={20} />
                                      </Box>
                                    )}

                                    {!detailLoading && detailError && (
                                      <Typography variant="body2" color="error.main">
                                        {detailError}
                                      </Typography>
                                    )}

                                    {!detailLoading && !detailError && (
                                      <Box sx={{ ...TABLE_PANEL_SX }}>
                                        <Table
                                          size="small"
                                          sx={{
                                            ...DATA_TABLE_SX,
                                            minWidth: 760,
                                          }}
                                        >
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Grade</TableCell>
                                              <TableCell align="right">Sales Amount</TableCell>
                                              <TableCell align="right">Pump Vol Sales</TableCell>
                                              <TableCell align="right">Tank Vol Sales</TableCell>
                                              <TableCell align="right">Pump Vol Variance</TableCell>
                                              <TableCell align="right">Opening Vol</TableCell>
                                              <TableCell align="right">Closing Dip</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {detailRows.map((detailRow, detailIndex) => (
                                              <TableRow key={`fuel-control-detail-${batchId}-${detailRow.grade_id}-${detailIndex}`}>
                                                <TableCell>{detailRow.grade_description || formatLabel(detailRow.grade_id) || '-'}</TableCell>
                                                <TableCell align="right">{formatAmount(detailRow.days_amount_sales)}</TableCell>
                                                <TableCell align="right">{formatAmount(detailRow.days_pump_vol_sales)}</TableCell>
                                                <TableCell align="right">{formatAmount(detailRow.days_tank_vol_sales)}</TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{
                                                    color: toNumber(detailRow.days_pump_vol_variance) < 0 ? 'error.main' : 'success.main',
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {formatAmount(detailRow.days_pump_vol_variance)}
                                                </TableCell>
                                                <TableCell align="right">{formatAmount(detailRow.opening_vol)}</TableCell>
                                                <TableCell align="right">{formatAmount(detailRow.closing_dip)}</TableCell>
                                              </TableRow>
                                            ))}

                                            {detailRows.length === 0 && (
                                              <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                  No fuel sales control detail rows for this batch.
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}

                    {!fuelControlLoading && fuelControlRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No fuel sales control data found for current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={fuelControlTotalRows}
                page={fuelControlPage - 1}
                onPageChange={(_, newPage) => setFuelControlPage(newPage + 1)}
                rowsPerPage={fuelControlRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setFuelControlRowsPerPage(parseInt(event.target.value, 10));
                  setFuelControlPage(1);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                sx={TABLE_PAGINATION_SX}
              />
            </Box>
          )}

          {activeTab === 'pump_variance' && (
            <Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.2}
                sx={TABLE_SECTION_HEADER_SX}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Pump Variance Table
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Rows: {integerFormatter.format(toNumber(pumpVarianceTotalRecords))}
                </Typography>
              </Stack>

              <Box sx={{ ...TABLE_PANEL_SX }}>
                <Table size="small" sx={DATA_TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Start Reading</TableCell>
                      <TableCell align="right">End Reading</TableCell>
                      <TableCell align="right">Calculated Sales Vol</TableCell>
                      <TableCell align="right">Actual Sales Vol</TableCell>
                      <TableCell align="right">Vol Variance</TableCell>
                      <TableCell align="right">Inc Unsettled Vol Variance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pumpVarianceLoading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    )}

                    {!pumpVarianceLoading &&
                      pumpVarianceRows.map((row, rowIndex) => {
                        const batchId = toNumber(row.batch_id || row.id);
                        const isExpanded = expandedPumpVarianceBatchId === batchId;
                        const detailRows = pumpVarianceDetailRowsByBatchId[batchId] || [];
                        const detailLoading = Boolean(pumpVarianceDetailLoadingByBatchId[batchId]);
                        const detailError = pumpVarianceDetailErrorByBatchId[batchId];

                        return (
                          <React.Fragment key={`pump-variance-summary-${batchId || row.batch_number}`}>
                            <TableRow
                              hover
                              onClick={() => handlePumpVarianceSummaryRowClick(row)}
                              sx={{
                                ...getStripedRowSx(rowIndex, { clickable: true }),
                                backgroundColor: isExpanded ? '#eaf2ff' : undefined,
                              }}
                            >
                              <TableCell sx={{ fontWeight: 700 }}>{row.batch_number || '-'}</TableCell>
                              <TableCell>{row.batch_date || '-'}</TableCell>
                              <TableCell align="right">{integerFormatter.format(toNumber(row.volume_start_reading))}</TableCell>
                              <TableCell align="right">{integerFormatter.format(toNumber(row.volume_end_reading))}</TableCell>
                              <TableCell align="right">{formatAmount(row.calculated_sales_volume)}</TableCell>
                              <TableCell align="right">{formatAmount(row.actual_sales_volume)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: toNumber(row.volume_variance) < 0 ? 'error.main' : 'success.main',
                                  fontWeight: 600,
                                }}
                              >
                                {formatAmount(row.volume_variance)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: toNumber(row.inc_unsettled_vol_variance) < 0 ? 'error.main' : 'success.main',
                                  fontWeight: 600,
                                }}
                              >
                                {formatAmount(row.inc_unsettled_vol_variance)}
                              </TableCell>
                            </TableRow>

                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={8} sx={{ py: 0, backgroundColor: '#f1f5f9', borderBottom: 0 }}>
                                  <Box sx={{ px: 1.5, py: 1.2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                                      Batch {row.batch_number || '-'} pump variance details
                                    </Typography>

                                    {detailLoading && (
                                      <Box sx={{ py: 1.2, display: 'flex', justifyContent: 'center' }}>
                                        <CircularProgress size={20} />
                                      </Box>
                                    )}

                                    {!detailLoading && detailError && (
                                      <Typography variant="body2" color="error.main">
                                        {detailError}
                                      </Typography>
                                    )}

                                    {!detailLoading && !detailError && (
                                      <Box sx={{ ...TABLE_PANEL_SX }}>
                                        <Table
                                          size="small"
                                          sx={{
                                            ...DATA_TABLE_SX,
                                            minWidth: 760,
                                          }}
                                        >
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Pump No</TableCell>
                                              <TableCell>Grade</TableCell>
                                              <TableCell align="right">Start Reading</TableCell>
                                              <TableCell align="right">End Reading</TableCell>
                                              <TableCell align="right">Calculated Sales Vol</TableCell>
                                              <TableCell align="right">Actual Sales Vol</TableCell>
                                              <TableCell align="right">Vol Variance</TableCell>
                                              <TableCell align="right">Inc Unsettled Vol Variance</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {detailRows.map((detailRow, detailIndex) => (
                                              <TableRow key={`pump-variance-detail-${batchId}-${detailRow.pump_no}-${detailIndex}`}>
                                                <TableCell>{detailRow.pump_no || '-'}</TableCell>
                                                <TableCell>{detailRow.grade_description || formatLabel(detailRow.grade_id) || '-'}</TableCell>
                                                <TableCell align="right">{integerFormatter.format(toNumber(detailRow.volume_start_reading))}</TableCell>
                                                <TableCell align="right">{integerFormatter.format(toNumber(detailRow.volume_end_reading))}</TableCell>
                                                <TableCell align="right">{formatAmount(detailRow.calculated_sales_volume)}</TableCell>
                                                <TableCell align="right">{formatAmount(detailRow.actual_sales_volume)}</TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{
                                                    color: toNumber(detailRow.volume_variance) < 0 ? 'error.main' : 'success.main',
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {formatAmount(detailRow.volume_variance)}
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{
                                                    color: toNumber(detailRow.inc_unsettled_vol_variance) < 0 ? 'error.main' : 'success.main',
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {formatAmount(detailRow.inc_unsettled_vol_variance)}
                                                </TableCell>
                                              </TableRow>
                                            ))}

                                            {detailRows.length === 0 && (
                                              <TableRow>
                                                <TableCell colSpan={8} align="center">
                                                  No pump variance detail rows for this batch.
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}

                    {!pumpVarianceLoading && pumpVarianceRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No pump variance data found for current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={pumpVarianceTotalRecords}
                page={pumpVariancePage - 1}
                onPageChange={(_, newPage) => setPumpVariancePage(newPage + 1)}
                rowsPerPage={pumpVarianceRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setPumpVarianceRowsPerPage(parseInt(event.target.value, 10));
                  setPumpVariancePage(1);
                }}
                rowsPerPageOptions={[15, 25, 50]}
                sx={TABLE_PAGINATION_SX}
              />
            </Box>
          )}
        </Paper>

        <Dialog open={openBatchModal} onClose={() => setOpenBatchModal(false)} fullWidth maxWidth="lg">
          <DialogTitle>
            {selectedBatchDetail?.batch_number
              ? `Batch #${selectedBatchDetail.batch_number} Breakdown`
              : 'Batch Breakdown'}
          </DialogTitle>
          <DialogContent dividers>
            {batchDetailLoading && (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </Stack>
            )}

            {!batchDetailLoading && batchDetailError && (
              <Typography color="error">{batchDetailError}</Typography>
            )}

            {!batchDetailLoading && !batchDetailError && selectedBatchDetail && (
              <>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip
                    label={`Depot: ${getDepotLabel(selectedBatchDetail.depot_id)} (ID ${selectedBatchDetail.depot_id})`}
                    size="small"
                    sx={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}
                  />
                  <Chip
                    label={`Batch Date: ${selectedBatchDetail.batch_date || '-'}`}
                    size="small"
                    sx={{ backgroundColor: '#e2e8f0', color: '#334155' }}
                  />
                  <Chip
                    label={`Net Variance: ${formatCurrency(selectedBatchDetail?.summary?.net_variance)}`}
                    size="small"
                    sx={{ backgroundColor: '#f1f5f9', color: '#334155' }}
                  />
                  <Chip
                    label={`Absolute Variance: ${formatCurrency(selectedBatchDetail?.summary?.absolute_variance)}`}
                    size="small"
                    sx={{ backgroundColor: '#f1f5f9', color: '#334155' }}
                  />
                </Stack>

                <Grid container spacing={1.2} sx={{ mb: 2 }}>
                  {batchDetailGroupSummary.map((group) => (
                    <Grid size={{ xs: 12, md: 6 }} key={`group-summary-${group.tender_group}`}>
                      <Card sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {group.tender_group}
                          </Typography>
                          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              Actual: {formatCurrency(group.actual)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Theoretical: {formatCurrency(group.theoretical)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: group.variance < 0 ? 'error.main' : 'success.main', fontWeight: 700 }}
                            >
                              Variance: {formatCurrency(group.variance)}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ ...TABLE_PANEL_SX }}>
                  <Table size="small" sx={DATA_TABLE_SX}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tender Group</TableCell>
                        <TableCell>Tender Type</TableCell>
                        <TableCell align="right">Cash In Drawer</TableCell>
                        <TableCell align="right">Lifts/Drops</TableCell>
                        <TableCell align="right">Actual</TableCell>
                        <TableCell align="right">Theoretical</TableCell>
                        <TableCell align="right">Variance</TableCell>
                        <TableCell align="right">Transfers</TableCell>
                        <TableCell align="right">Adjustments</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batchDetailRows.map((row, rowIndex) => (
                        <TableRow
                          key={`batch-detail-row-${row.tender_group}-${row.tender_type}`}
                          sx={getStripedRowSx(rowIndex, { disableHover: true })}
                        >
                          <TableCell>{row.tender_group || '-'}</TableCell>
                          <TableCell>{row.tender_type || '-'}</TableCell>
                          <TableCell align="right">{formatAmount(row.cash_in_drawer)}</TableCell>
                          <TableCell align="right">{formatAmount(row.lifts_drops)}</TableCell>
                          <TableCell align="right">{formatAmount(row.actual)}</TableCell>
                          <TableCell align="right">{formatAmount(row.theoretical)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: toNumber(row.variance) < 0 ? 'error.main' : 'success.main', fontWeight: 700 }}
                          >
                            {formatAmount(row.variance)}
                          </TableCell>
                          <TableCell align="right">{formatAmount(row.transfers)}</TableCell>
                          <TableCell align="right">{formatAmount(row.adjustments)}</TableCell>
                        </TableRow>
                      ))}
                      {batchDetailRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No detail rows found for this batch.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={handleInspectBatchClick}
              disabled={!selectedBatchDetail?.batch_id}
            >
              Inspect Batch
            </Button>
            <Button onClick={() => setOpenBatchModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openInspectBatchModal}
          onClose={() => setOpenInspectBatchModal(false)}
          fullWidth
          maxWidth="xl"
        >
          <DialogTitle>
            {inspectBatchData?.metadata?.batch_number
              ? `Inspect Batch #${inspectBatchData.metadata.batch_number}`
              : 'Inspect Batch'}
          </DialogTitle>
          <DialogContent dividers>
            {inspectBatchLoading && (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </Stack>
            )}

            {!inspectBatchLoading && inspectBatchError && (
              <Typography color="error">{inspectBatchError}</Typography>
            )}

            {!inspectBatchLoading && !inspectBatchError && (
              <>
                <Grid container spacing={1.2} sx={{ mb: 2 }}>
                  {inspectBatchMetadataCards.map((card) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={`inspect-card-${card.label}`}>
                      <Card sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <CardContent sx={{ py: 1.2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                            {card.label}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, wordBreak: 'break-word' }}>
                            {card.value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <TextField
                  fullWidth
                  multiline
                  minRows={16}
                  label="Raw RPT File (read-only)"
                  value={inspectBatchRawContent}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontFamily: 'monospace',
                      fontSize: 12.5,
                      whiteSpace: 'pre',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Message for requests"
                  placeholder="Add your request note for follow-up, investigation, or support."
                  value={inspectBatchRequestMessage}
                  onChange={(event) => setInspectBatchRequestMessage(event.target.value)}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenInspectBatchModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {showDepotExecutiveActions && (
          <>
            <BuyModal
              openModal={quickActionModalState.buy}
              toggleModalState={toggleQuickActionModalState}
              setUpdateStats={() => {
                fetchSummaryCards();
                fetchBatchBreakdownTable();
              }}
              setUpdateAllOders={() => {}}
              orderLimits={quickActionOrderLimits}
              showSnackbar={showSnackbar}
              supplierEndpoint={depotRoleSupplierEndpoint || undefined}
            />

            <InterDepotTankTransfer
              openModal={quickActionModalState.tanks}
              handleCloseModal={() => setQuickActionModal('tanks', false)}
            />

            <TransportCompany
              openModal={quickActionModalState.addClient}
              handleCloseModal={() => setQuickActionModal('addClient', false)}
              handleSubmitCallback={() => setQuickActionModal('addClient', false)}
            />

            <NewProductLine
              openModal={quickActionModalState.addProduct}
              handleCloseModal={() => setQuickActionModal('addProduct', false)}
              handleSubmitCallback={() => setQuickActionModal('addProduct', false)}
            />
          </>
        )}
      </Container>
    </>
  );
}
